import { Accordion, Anchor, Badge, Button, Card, Group, Select, SimpleGrid, Stack, Table, Text, TextInput, ThemeIcon, Timeline, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCheck, IconDownload, IconSearch, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, download } from '../../lib/api';
import { STATUS, fmtDate, fmtDateTime, score } from '../../lib/format';
import type { AssignmentEvent, Department, ScoreRow } from '../../lib/types';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';
import { StatusBadge } from '../../ui/StatusBadge';

export function ResultsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const filter = { courseId: params.get('courseId') ?? '', department: params.get('department') ?? '', status: params.get('status') ?? '', dueFrom: params.get('dueFrom') ?? '', dueTo: params.get('dueTo') ?? '', q };
  const qs = new URLSearchParams(Object.entries(filter).filter(([, v]) => v)).toString();
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n, { replace: true });
  };
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.get<Department[]>('/admin/departments') });
  const rows = useQuery({ queryKey: ['results', qs], queryFn: () => api.get<ScoreRow[]>(`/admin/results?${qs}`) });
  const courses = [...new Map((rows.data ?? []).map((r) => [r.courseId, r.course])).entries()];
  const [exporting, setExporting] = useState(false);

  return (
    <>
      <PageHeader
        title="Результаты"
        subtitle="Индивидуальные баллы видит только роль «Результаты». Открытие карточки сотрудника пишется в журнал аудита."
        actions={<Button leftSection={<IconDownload size={16} />} variant="default" loading={exporting} onClick={async () => {
          setExporting(true);
          try { await download(`/admin/results.csv?${qs}`, `chiron-results-${new Date().toISOString().slice(0, 10)}.csv`); } catch (e) { notifications.show({ color: 'red', message: (e as Error).message }); } finally { setExporting(false); }
        }}>Выгрузить CSV</Button>}
      />
      <Card withBorder radius="lg" padding="md" mb="md">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput label="Поиск" placeholder="ФИО, email, табельный" leftSection={<IconSearch size={14} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={210} />
          <Select label="Курс" placeholder="Все" clearable value={filter.courseId || null} onChange={(v) => set('courseId', v)} w={220} data={courses.map(([id, t]) => ({ value: String(id), label: t }))} />
          <Select label="Подразделение" placeholder="Все" clearable value={filter.department || null} onChange={(v) => set('department', v)} w={170} data={(departments.data ?? []).map((d) => d.name)} />
          <Select label="Статус" placeholder="Все" clearable value={filter.status || null} onChange={(v) => set('status', v)} w={210} data={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))} />
          <DatePickerInput type="range" label="Срок" placeholder="Любой" clearable w={230} valueFormat="D MMM YYYY"
            value={[filter.dueFrom ? dayjs(filter.dueFrom).format('YYYY-MM-DD') : null, filter.dueTo ? dayjs(filter.dueTo).format('YYYY-MM-DD') : null]}
            onChange={([from, to]) => {
              const n = new URLSearchParams(params);
              if (from) n.set('dueFrom', dayjs(from).startOf('day').toISOString()); else n.delete('dueFrom');
              if (to) n.set('dueTo', dayjs(to).endOf('day').toISOString()); else n.delete('dueTo');
              setParams(n, { replace: true });
            }} />
        </Group>
      </Card>
      <QueryState loading={rows.isLoading} error={rows.error}>
        {() => (
          <Card withBorder radius="lg" p={0}>
            <Table.ScrollContainer minWidth={1150}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" striped>
                <Table.Thead>
                  <Table.Tr><Table.Th>Сотрудник</Table.Th><Table.Th>Курс</Table.Th><Table.Th>Исходный срок</Table.Th><Table.Th>Текущий срок</Table.Th><Table.Th>Статус</Table.Th><Table.Th>1-я попытка</Table.Th><Table.Th>Лучший</Table.Th><Table.Th>Попытки</Table.Th><Table.Th>Завершён</Table.Th></Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.data!.map((r) => (
                    <Table.Tr key={r.id} style={{ cursor: r.anonymized ? 'default' : 'pointer' }} onClick={() => !r.anonymized && navigate(`/admin/results/${r.id}`)}>
                      <Table.Td><Text size="sm" fw={500} c={r.anonymized ? 'dimmed' : undefined}>{r.fullName}</Text><Text size="xs" c="dimmed">{r.department ?? '-'}{r.employeeId ? ` · ${r.employeeId}` : ''}</Text></Table.Td>
                      <Table.Td><Text size="sm">{r.course}</Text><Text size="xs" c="dimmed">v{r.version}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(r.originalDueAt, r.timezone)}</Text></Table.Td>
                      <Table.Td><Text size="sm" c={r.rescheduled ? 'blue' : undefined}>{fmtDate(r.dueAt, r.timezone)}</Text></Table.Td>
                      <Table.Td miw={180}><StatusBadge status={r.status} /></Table.Td>
                      <Table.Td><Text size="sm">{score(r.firstCorrect, r.questionCount)}</Text></Table.Td>
                      <Table.Td><Text size="sm" fw={600}>{score(r.bestCorrect, r.questionCount)}</Text></Table.Td>
                      <Table.Td><Text size="sm">{r.attemptsUsed}/{r.attemptsLimit}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(r.completedAt)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                  {rows.data!.length === 0 && <Table.Tr><Table.Td colSpan={9}><Text c="dimmed" size="sm" ta="center" py="md">Ничего не найдено</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}
      </QueryState>
      {rows.data && <Text size="xs" c="dimmed" mt="xs">Записей: {rows.data.length}. Порог сдачи - число верных ответов из заданных.</Text>}
    </>
  );
}

interface Detail extends ScoreRow {
  events: AssignmentEvent[];
  attempts: { attemptNo: number; state: string; startedAt: string; submittedAt: string | null; correctCount: number | null; questionCount: number; passCount: number; passed: boolean | null; answered: number; responses: { text: string; chosen: string | null; correctAnswer: string | null; isCorrect: boolean; lessonTitle: string | null }[] }[];
}

const EVENT_RU = { RESCHEDULE: 'Перенос срока', CANCEL: 'Отмена', ADD_ATTEMPTS: 'Добавлены попытки' } as const;

export function ResultDetailPage() {
  const id = Number(useParams().id);
  const q = useQuery({ queryKey: ['result-detail', id], queryFn: () => api.get<Detail>(`/admin/results/${id}`) });
  return (
    <QueryState loading={q.isLoading} error={q.error}>
      {() => {
        const d = q.data!;
        return (
          <Stack>
            <Anchor component={Link} to="/admin/results" size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> Результаты</Anchor>
            <div>
              <StatusBadge status={d.status} size="md" />
              <Title order={2} mt={4}>{d.fullName}</Title>
              <Text c="dimmed">{d.department ?? '-'} · {d.employeeId ?? ''} · {d.email ?? ''}</Text>
            </div>
            <SimpleGrid cols={{ base: 2, md: 5 }}>
              {[
                ['Курс', `${d.course} · v${d.version}`],
                ['Исходный срок', fmtDateTime(d.originalDueAt, d.timezone)],
                ['Текущий срок', fmtDateTime(d.dueAt, d.timezone)],
                ['Первая / лучшая', `${score(d.firstCorrect, d.questionCount)} / ${score(d.bestCorrect, d.questionCount)}`],
                ['Попытки', `${d.attemptsUsed} из ${d.attemptsLimit}`],
              ].map(([l, v]) => <Card key={l} withBorder radius="md" padding="sm"><Text size="xs" c="dimmed">{l}</Text><Text size="sm" fw={600}>{v}</Text></Card>)}
            </SimpleGrid>
            <Card withBorder radius="lg" padding="lg">
              <Title order={4} mb="md">История попыток</Title>
              {d.attempts.length === 0 && <Text size="sm" c="dimmed">Попыток не было</Text>}
              <Accordion variant="separated" radius="md">
                {d.attempts.map((t) => (
                  <Accordion.Item key={t.attemptNo} value={String(t.attemptNo)}>
                    <Accordion.Control>
                      <Group gap="sm">
                        <Text fw={600} size="sm">Попытка {t.attemptNo}</Text>
                        {t.state === 'SUBMITTED'
                          ? <Badge color={t.passed ? 'teal' : 'red'} variant="light">{t.correctCount}/{t.questionCount} (порог {t.passCount})</Badge>
                          : <Badge color="gray" variant="outline">не отправлена · отвечено {t.answered}/{t.questionCount}</Badge>}
                        <Text size="xs" c="dimmed">{fmtDateTime(t.submittedAt ?? t.startedAt)}</Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap={8}>
                        {t.responses.map((r, i) => (
                          <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
                            <ThemeIcon size={18} radius="xl" color={r.isCorrect ? 'teal' : 'red'} mt={2}>{r.isCorrect ? <IconCheck size={11} /> : <IconX size={11} />}</ThemeIcon>
                            <div>
                              <Text size="sm">{r.text}</Text>
                              <Text size="xs" c={r.isCorrect ? 'dimmed' : 'red'}>Ответ: {r.chosen ?? '-'}</Text>
                              {!r.isCorrect && <Text size="xs" c="teal">Верно: {r.correctAnswer}{r.lessonTitle ? ` · урок «${r.lessonTitle}»` : ''}</Text>}
                            </div>
                          </Group>
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Card>
            {d.events.length > 0 && (
              <Card withBorder radius="lg" padding="lg">
                <Title order={5} mb="md">События назначения</Title>
                <Timeline bulletSize={18}>
                  {d.events.map((e) => (
                    <Timeline.Item key={e.id} title={<Text size="sm" fw={600}>{EVENT_RU[e.type]}{e.type === 'ADD_ATTEMPTS' ? ` +${e.value}` : ''}{e.type === 'RESCHEDULE' && e.value ? ` → ${fmtDate(e.value, d.timezone)}` : ''}</Text>}>
                      <Text size="sm">«{e.reason}»</Text>
                      <Text size="xs" c="dimmed">{e.actorName} · {fmtDateTime(e.occurredAt)}</Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            )}
          </Stack>
        );
      }}
    </QueryState>
  );
}
